/* ===================================================================
   Gallery Triptych — slot-based three-panel carousel
   =================================================================== */

(() => {
  const TRANSITION_MS = 600;
  const SNAP_DURATION = 300;
  const DEAD_ZONE = 10;

  class GalleryTriptych extends HTMLElement {

    /* ----------------------------------------------------------
       Overrideable hooks — subclasses customise these getters
       instead of duplicating methods.
       ---------------------------------------------------------- */

    /** CSS class added to cloned elements during animation */
    get _cloneClass() { return 'gt__clone'; }

    /** CSS class that disables CSS transitions for one frame */
    get _noTransClass() { return 'gt--no-transition'; }

    /* ----------------------------------------------------------
       Lifecycle
       ---------------------------------------------------------- */

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.gt__item'));
      if (this.items.length < 1) return;
      this.stage = this.querySelector('.gt__stage');
      this._initCarousel();
      this._initReveal();
    }

    /* ----------------------------------------------------------
       Entrance: fade the whole gallery in when it scrolls into
       view (it sits below the fold on the athlete page). Opacity-
       only, so it never fights the carousel's transform-based slot
       animations. The hidden start state is CSS-driven (`.js .gt`,
       see gallery-triptych.css) so it's painted before this
       deferred script runs and never flashes; here we just add
       `gt--in-view` to trigger the fade.
       ---------------------------------------------------------- */
    _initReveal() {
      const reduceMotion =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) return;

      // Release on two frames so the opacity:0 start state always paints first
      // — otherwise a gallery already in view on load (above the fold) would
      // jump straight to opacity:1 with no transition.
      const reveal = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.classList.add('gt--in-view'));
        });
      };

      if (!('IntersectionObserver' in window)) {
        reveal();
        return;
      }

      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            reveal();
            obs.unobserve(entry.target);
          });
        },
        // Trigger once at least 50% of the gallery is in the viewport.
        { threshold: 0.5 }
      );
      observer.observe(this);
    }

    disconnectedCallback() {
      if (this._keyHandler) document.removeEventListener('keydown', this._keyHandler);
      if (this._dragRAF) cancelAnimationFrame(this._dragRAF);
      if (this._snapRAF) cancelAnimationFrame(this._snapRAF);
      this._removeClones();
    }

    /* ----------------------------------------------------------
       Shared carousel initialisation
       Subclasses set this.items and this.stage, then call this.
       ---------------------------------------------------------- */

    _initCarousel() {
      this.centerIndex = 0;
      this.isAnimating = false;
      this._scrollPos = 0;
      this._handleDisp = 0;
      this._dragActive = false;
      this._dragRAF = null;
      this._snapRAF = null;
      this._cloneMap = null;

      this._bindHandleControl();
      this._bindExtras();
      this._bindKeyboard();
      this._bindTouch();
      this._assignInitialSlots();
    }

    /**
     * Hook: called from _initCarousel for any extra bindings.
     * GalleryTriptych binds click-to-expand content overlays.
     * Subclasses override this to replace with their own bindings.
     */
    _bindExtras() {
      this._bindOverlays();
    }

    /**
     * Hook: called after every discrete navigation step completes
     * (including snap-to-nearest at the end of a drag).
     * Subclasses override to react to index changes (e.g. update a counter).
     */
    _onNavigate() {}

    /* ----------------------------------------------------------
       Slot assignment
       ---------------------------------------------------------- */

    _wrap(i) {
      const len = this.items.length;
      return ((i % len) + len) % len;
    }

    _assignInitialSlots() {
      this._assignSlotsFor(this.centerIndex);
    }

    _assignSlotsFor(center) {
      for (const item of this.items) {
        item.dataset.slot = 'hidden';
      }
      if (this.items.length === 1) {
        this.items[center].dataset.slot = 'center';
        return;
      }
      if (this.items.length === 2) {
        this.items[center].dataset.slot = 'center';
        this.items[this._wrap(center + 1)].dataset.slot = 'right';
        return;
      }
      this.items[this._wrap(center - 1)].dataset.slot = 'left';
      this.items[center].dataset.slot = 'center';
      this.items[this._wrap(center + 1)].dataset.slot = 'right';
    }

    /* ----------------------------------------------------------
       Discrete navigation (buttons, touch swipe)
       ---------------------------------------------------------- */

    _makeExitClone(item, exitSlot) {
      const clone = item.cloneNode(true);
      clone.removeAttribute('id');
      clone.classList.add(this._cloneClass);
      clone.setAttribute('aria-hidden', 'true');
      // The clone ROOT as well as its descendants. querySelectorAll does not match
      // the element it is called on, and the originals are given tabindex="0" +
      // role="button" in _setup, which cloneNode(true) copies — so every clone sat
      // in the tab order under aria-hidden="true" (axe aria-hidden-focus). A
      // keyboard user tabbed onto invisible duplicates of tiles already passed.
      // Same bug, same fix as assets/feature-highlight-gallery.js.
      clone.setAttribute('tabindex', '-1');
      for (const el of clone.querySelectorAll('button, a, [tabindex]')) {
        el.setAttribute('tabindex', '-1');
      }
      clone.dataset.slot = item.dataset.slot;
      this.stage.appendChild(clone);
      clone.offsetHeight;
      clone.dataset.slot = exitSlot;
      return clone;
    }

    _navigateRight() {
      if (this.isAnimating || this._dragActive || this.items.length < 3) return;
      this.isAnimating = true;
      this._closeAllOverlays();

      const oldLeftIdx = this._wrap(this.centerIndex - 1);
      const newRightIdx = this._wrap(this.centerIndex + 2);
      const recycled = (oldLeftIdx === newRightIdx);
      let exitClone = null;

      if (recycled) {
        exitClone = this._makeExitClone(this.items[oldLeftIdx], 'off-left');
      }

      const incoming = this.items[newRightIdx];
      incoming.classList.add(this._noTransClass);
      incoming.dataset.slot = 'off-right';
      incoming.offsetHeight;
      incoming.classList.remove(this._noTransClass);

      this.centerIndex = this._wrap(this.centerIndex + 1);

      if (!recycled) {
        this.items[oldLeftIdx].dataset.slot = 'off-left';
      }
      this.items[this._wrap(this.centerIndex - 1)].dataset.slot = 'left';
      this.items[this.centerIndex].dataset.slot = 'center';
      incoming.dataset.slot = 'right';

      setTimeout(() => {
        if (!recycled) {
          this.items[oldLeftIdx].dataset.slot = 'hidden';
        }
        if (exitClone) exitClone.remove();
        this.isAnimating = false;
        this._onNavigate();
      }, TRANSITION_MS + 50);
    }

    _navigateLeft() {
      if (this.isAnimating || this._dragActive || this.items.length < 3) return;
      this.isAnimating = true;
      this._closeAllOverlays();

      const oldRightIdx = this._wrap(this.centerIndex + 1);
      const newLeftIdx = this._wrap(this.centerIndex - 2);
      const recycled = (oldRightIdx === newLeftIdx);
      let exitClone = null;

      if (recycled) {
        exitClone = this._makeExitClone(this.items[oldRightIdx], 'off-right');
      }

      const incoming = this.items[newLeftIdx];
      incoming.classList.add(this._noTransClass);
      incoming.dataset.slot = 'off-left';
      incoming.offsetHeight;
      incoming.classList.remove(this._noTransClass);

      this.centerIndex = this._wrap(this.centerIndex - 1);

      if (!recycled) {
        this.items[oldRightIdx].dataset.slot = 'off-right';
      }
      this.items[this._wrap(this.centerIndex + 1)].dataset.slot = 'right';
      this.items[this.centerIndex].dataset.slot = 'center';
      incoming.dataset.slot = 'left';

      setTimeout(() => {
        if (!recycled) {
          this.items[oldRightIdx].dataset.slot = 'hidden';
        }
        if (exitClone) exitClone.remove();
        this.isAnimating = false;
        this._onNavigate();
      }, TRANSITION_MS + 50);
    }

    /* ----------------------------------------------------------
       Continuous drag rendering
       ---------------------------------------------------------- */

    _computeSlots() {
      const sw = this.stage.offsetWidth;
      const sh = this.stage.offsetHeight;

      if (window.innerWidth < 990) {
        this._slotDefs = [
          { l: -2 * sw, w: sw, h: sh },
          { l: -sw,     w: sw, h: sh },
          { l: 0,       w: sw, h: sh },
          { l: sw,      w: sw, h: sh },
          { l: 2 * sw,  w: sw, h: sh }
        ];
        return;
      }

      const margin = (window.innerWidth - sw) / 2;
      const sideW = 0.2816 * sw;
      const sideH = 0.6726 * sh;
      const ctrW = 0.4181 * sw;
      const ctrH = sh;
      const ctrX = 0.2910 * sw;
      const rightX = 0.7184 * sw;

      this._slotDefs = [
        { l: -(sideW + margin), w: sideW, h: sideH },
        { l: 0,                 w: sideW, h: sideH },
        { l: ctrX,              w: ctrW,  h: ctrH  },
        { l: rightX,            w: sideW, h: sideH },
        { l: sw + margin,       w: sideW, h: sideH }
      ];
    }

    _lerp(a, b, t) { return a + (b - a) * t; }

    _createClones() {
      this._removeClones();
      this._cloneMap = {};
      for (let i = 0; i < this.items.length; i++) {
        const clone = this.items[i].cloneNode(true);
        clone.removeAttribute('id');
        clone.classList.add(this._cloneClass);
        clone.style.display = 'none';
        // Root included — see the note in _makeExitClone above.
        clone.setAttribute('tabindex', '-1');
        for (const el of clone.querySelectorAll('button, a, [tabindex]')) {
          el.setAttribute('tabindex', '-1');
        }
        clone.setAttribute('aria-hidden', 'true');
        this.stage.appendChild(clone);
        this._cloneMap[i] = clone;
      }
    }

    _removeClones() {
      if (this._cloneMap) {
        for (const k in this._cloneMap) {
          this._cloneMap[k].remove();
        }
        this._cloneMap = null;
      }
    }

    _applySlotStyle(el, from, to, f, si) {
      const isMobile = window.innerWidth < 990;
      const st = el.style;
      st.display = '';
      st.position = 'absolute';
      st.top = isMobile ? '0' : '50%';
      st.transform = isMobile ? 'none' : 'translateY(-50%)';
      st.transition = 'none';
      st.overflow = 'hidden';
      st.left = `${this._lerp(from.l, to.l, f)}px`;
      st.width = `${this._lerp(from.w, to.w, f)}px`;
      st.height = `${this._lerp(from.h, to.h, f)}px`;
      st.zIndex = (si === 2) ? '2' : '1';
    }

    _renderAtPosition(pos) {
      const len = this.items.length;
      const n = Math.floor(pos);
      const f = pos - n;
      const S = this._slotDefs;

      const assignments = [
        { offset: -1, si: 1 },
        { offset: 0,  si: 2 },
        { offset: 1,  si: 3 },
        { offset: 2,  si: 4 }
      ];

      for (let i = 0; i < len; i++) {
        this.items[i].style.display = 'none';
        this.items[i].removeAttribute('data-slot');
        if (this._cloneMap && this._cloneMap[i]) {
          this._cloneMap[i].style.display = 'none';
        }
      }

      const placed = {};

      for (const { offset, si } of assignments) {
        const idx = this._wrap(n + offset);
        const from = S[si];
        let to = S[si - 1];
        if (!to) to = from;

        let el;
        if (placed[idx]) {
          el = this._cloneMap ? this._cloneMap[idx] : null;
          if (!el) continue;
        } else {
          placed[idx] = true;
          el = this.items[idx];
        }

        this._applySlotStyle(el, from, to, f, si);
      }
    }

    _clearInlineStyles() {
      this._removeClones();
      for (const item of this.items) {
        item.style.cssText = '';
      }
    }

    /* ----------------------------------------------------------
       Drag loop (velocity-based continuous motion)
       ---------------------------------------------------------- */

    _startDrag() {
      if (this.items.length < 3) return;
      this._closeAllOverlays();
      this._dragActive = true;
      this._scrollPos = this.centerIndex;
      this._handleDisp = 0;
      this._lastFrame = performance.now();

      if (this._snapRAF) { cancelAnimationFrame(this._snapRAF); this._snapRAF = null; }

      this._computeSlots();
      this._createClones();
      this._renderAtPosition(this._scrollPos);

      const len = this.items.length;
      const frame = (now) => {
        if (!this._dragActive) return;
        const dt = Math.min(0.1, (now - this._lastFrame) / 1000);
        this._lastFrame = now;

        const d = this._handleDisp;
        const abs = Math.abs(d) - DEAD_ZONE;
        if (abs > 0) {
          const speed = abs * abs * 0.00025;
          const dir = d > 0 ? 1 : -1;
          this._scrollPos += dir * speed * dt;
          this._scrollPos = ((this._scrollPos % len) + len) % len;
        }

        this._renderAtPosition(this._scrollPos);
        this._dragRAF = requestAnimationFrame(frame);
      };
      this._dragRAF = requestAnimationFrame(frame);
    }

    _stopDrag() {
      if (!this._dragActive) return;
      this._dragActive = false;
      if (this._dragRAF) { cancelAnimationFrame(this._dragRAF); this._dragRAF = null; }
      this._snapToNearest();
    }

    _snapToNearest() {
      const len = this.items.length;
      const pos = ((this._scrollPos % len) + len) % len;
      let target = Math.round(pos);
      if (target >= len) target = 0;

      let diff = target - pos;
      if (diff > len / 2) diff -= len;
      if (diff < -len / 2) diff += len;

      const start = pos;
      const end = pos + diff;
      const t0 = performance.now();

      const frame = (now) => {
        const t = Math.min(1, (now - t0) / SNAP_DURATION);
        const ease = 1 - Math.pow(1 - t, 3);
        this._scrollPos = start + (end - start) * ease;
        this._renderAtPosition(((this._scrollPos % len) + len) % len);

        if (t < 1) {
          this._snapRAF = requestAnimationFrame(frame);
        } else {
          this.centerIndex = ((target % len) + len) % len;
          for (const item of this.items) {
            item.classList.add(this._noTransClass);
          }
          this._clearInlineStyles();
          this._assignSlotsFor(this.centerIndex);
          this.items[0].offsetHeight;
          for (const item of this.items) {
            item.classList.remove(this._noTransClass);
          }
          this._snapRAF = null;
          this.isAnimating = false;
          this._onNavigate();
        }
      };
      this.isAnimating = true;
      this._snapRAF = requestAnimationFrame(frame);
    }

    /* ----------------------------------------------------------
       Handle-control integration
       ---------------------------------------------------------- */

    _bindHandleControl() {
      this.addEventListener('handle:left', () => { this._navigateLeft(); });
      this.addEventListener('handle:right', () => { this._navigateRight(); });

      this.addEventListener('handle:dragstart', () => {
        this._startDrag();
      });

      this.addEventListener('handle:dragmove', (e) => {
        this._handleDisp = e.detail.displacement;
      });

      this.addEventListener('handle:dragend', () => {
        this._stopDrag();
      });
    }

    /* ----------------------------------------------------------
       Content overlays (click-to-expand panels on center item)
       ---------------------------------------------------------- */

    _bindOverlays() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('handle-control')) return;

        const item = e.target.closest('.gt__item');

        if (!item) {
          if (this.classList.contains('has-overlay-open')) {
            this._closeAllOverlays();
          }
          return;
        }

        if (item.dataset.slot !== 'center') return;

        const isOpen = item.classList.contains('has-overlay-open');

        if (isOpen) {
          if (!e.target.closest('a')) {
            this._closeOverlay(item);
          }
        } else {
          this._openOverlay(item);
        }
      });

      for (const item of this.items) {
        if (!item.getAttribute('tabindex')) item.setAttribute('tabindex', '0');
        if (!item.getAttribute('role')) item.setAttribute('role', 'button');
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            item.click();
          }
        });
      }
    }

    _openOverlay(item) {
      item.classList.add('has-overlay-open');
      this.classList.add('has-overlay-open');
      this._setExpanded(item, true);
    }

    _closeOverlay(item) {
      item.classList.remove('has-overlay-open');
      this.classList.remove('has-overlay-open');
      this._setExpanded(item, false);
    }

    _closeAllOverlays() {
      for (const item of this.querySelectorAll('.gt__item.has-overlay-open')) {
        this._closeOverlay(item);
      }
    }

    _setExpanded(item, state) {
      const btn = item.querySelector('.gt__item-action');
      if (btn) btn.setAttribute('aria-expanded', String(state));
      const overlay = item.querySelector('.gt__feature-overlay, .gt__product-overlay');
      if (overlay) overlay.setAttribute('aria-hidden', String(!state));
    }

    /* ----------------------------------------------------------
       Keyboard
       ---------------------------------------------------------- */

    _bindKeyboard() {
      this._keyHandler = (e) => {
        if (e.key === 'Escape') this._closeAllOverlays();
      };
      document.addEventListener('keydown', this._keyHandler);
    }

    /* ----------------------------------------------------------
       Touch swipe
       ---------------------------------------------------------- */

    _bindTouch() {
      let startX = 0;
      let startY = 0;
      let swiping = false;

      this.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        swiping = true;
      }, { passive: true });

      this.addEventListener('touchmove', (e) => {
        if (!swiping || e.touches.length !== 1) return;
        const dx = e.touches[0].clientX - startX;
        const dy = e.touches[0].clientY - startY;
        if (Math.abs(dy) > Math.abs(dx)) {
          swiping = false;
          return;
        }
        if (Math.abs(dx) > 40) {
          swiping = false;
          if (dx < 0) this._navigateRight();
          else this._navigateLeft();
        }
      }, { passive: true });

      this.addEventListener('touchend', () => { swiping = false; }, { passive: true });
    }
  }

  if (!customElements.get('gallery-triptych')) {
    customElements.define('gallery-triptych', GalleryTriptych);
  }
})();
